import React, { useState, useEffect } from "react";
import { Form, Label } from "reactstrap";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  Button,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Paper,
  createMuiTheme,
} from "@material-ui/core";
import {
  MuiPickersUtilsProvider,
  KeyboardTimePicker,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import LocalAirportIcon from "@material-ui/icons/LocalAirport";
import DateFnsUtils from "@date-io/date-fns";
import { withFormik } from "formik";
import Yup from "yup";
import { ThemeProvider as MuiThemeProvider } from "@material-ui/core/styles";
import colors from "../constants/colors";
import countries from "../constants/countries";
import usstates from "../constants/usstates";
import airlines from "../constants/airlines";
import { isMobile } from "react-device-detect";
import { onGetAirports } from "../api/BaseAPI";
import Schedule from "@material-ui/icons/Schedule";

// const incidents = [
//   {
//     value: -1,
//     label: "- -",
//   },
//   {
//     value: 1,
//     label: "Lost",
//   },
//   {
//     value: 2,
//     label: "Found",
//   },
// ];

const locations = [
  {
    value: -1,
    label: "- -",
  },
  {
    value: 1,
    label: "TSA Security Checkpoint",
  },
  {
    value: 2,
    label: "Outside Airport",
  },
  {
    value: 3,
    label: "Inside Terminal",
  },
  {
    value: 4,
    label: "Onboard Airplane",
  },
  {
    value: 100,
    label: "Not Sure",
  },
];

const categories = [
  {
    value: -1,
    label: "- -",
  },
  {
    value: 1,
    label: "Art Work",
  },
  {
    value: 2,
    label: "Camera",
  },
  {
    value: 3,
    label: "Cell Phone",
  },
  {
    value: 4,
    label: "Laptop/Computer",
  },
  {
    value: 5,
    label: "Electronics",
  },
  {
    value: 6,
    label: "Employee Badge",
  },
  {
    value: 7,
    label: "Art Work",
  },
  {
    value: 8,
    label: "Gift Certificate / Cupon",
  },
  {
    value: 9,
    label: "Jewelry",
  },
  {
    value: 10,
    label: "Keys",
  },
  {
    value: 11,
    label: "Luggage",
  },
  {
    value: 12,
    label: "Money Clip",
  },
  {
    value: 13,
    label: "Pen",
  },
  {
    value: 14,
    label: "Religious Article",
  },
  {
    value: 15,
    label: "Sporting Equipment",
  },
  {
    value: 16,
    label: "Wallet",
  },
  {
    value: 17,
    label: "Watch",
  },
  {
    value: 100,
    label: "Other",
  },
];

const CssTextField = withStyles({
  root: {
    "& label.Mui-focused": {
      color: colors.primary,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: colors.primary,
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: colors.primary,
      },
      "&:hover fieldset": {
        borderColor: colors.primary,
      },
      "&.Mui-focused fieldset": {
        borderColor: colors.primary,
      },
    },
  },
})(TextField);

export const customTheme = createMuiTheme({
  overrides: {
    MuiOutlinedInput: {
      root: {
        position: "relative",
        "& $notchedOutline": {
          borderColor: colors.primary,
        },
        "&:hover:not($disabled):not($focused):not($error) $notchedOutline": {
          borderColor: colors.primary,
          // Reset on touch devices, it doesn't add specificity
          "@media (hover: none)": {
            borderColor: colors.primary,
          },
        },
        "&$focused $notchedOutline": {
          borderColor: colors.primary,
          borderWidth: 1,
        },
      },
    },
    MuiFormLabel: {
      root: {
        "&$focused": {
          color: colors.primary,
        },
      },
    },
  },
  palette: {
    primary: {
      main: colors.primary,
    },
    secondary: {
      main: colors.primary,
    },
  },
});

const useStyles = makeStyles((theme) => ({
  boxWrapper: {
    marginBottom: "55px",
    minHeight: "calc(26vh + 260px)",
    borderBottomRightRadius: "4px",
    borderBottomLeftRadius: "4px",
  },
  root: {
    "& .MuiTextField-root": {
      // margin: theme.spacing(0.5),
      // width: "108ch",
    },
  },
  container: {
    padding: theme.spacing(2),
  },
  form: {
    padding: theme.spacing(1),
  },
  formItem: {
    "& .MuiTextField-root": {
      margin: theme.spacing(1),
      width: isMobile ? "95%" : "48.5%",
    },
  },
}));

const TSAForm = ({
  values,
  handleChange,
  handleSubmit,
  errors,
  touched,
  isSubmitting,
}) => {
  const classes = useStyles();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(-1);
  const [selectedCategory, setSelectedCategory] = useState(-1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(235);
  const [selectedState, setSelectedState] = useState();
  const [open, setOpen] = useState(false);
  const [airportOptions, setAirportOptions] = useState([]);
  const [airportKeyword, setAirportKeyword] = useState("");
  const loading = open && airportOptions.length === 0 && airportKeyword > 1;
  const [openDate, setOpenDate] = useState(false);
  const [openTime, setOpenTime] = useState(false);

  const handleAirport = (event, value) => {
    values.airport = value.airport_id;
    if (values.airport > 0) {
      delete errors["airport"];
    } else {
      errors.airport = "Airport is required";
    }
  };

  const handleDate = (date) => {
    values.date = date;
    if (values.date) {
      delete errors["date"];
    } else {
      errors.date = "Date is required";
    }
    setSelectedDate(values.date);
  };

  const handleTime = (time) => {
    values.time = time;
    if (values.time) {
      delete errors["time"];
    } else {
      errors.time = "Time is required";
    }
    setSelectedTime(values.time);
  };

  const handleLocation = (event, value) => {
    values.location = parseInt(event.target.value);
    if (values.location > 0) {
      delete errors["location"];
    } else {
      errors.location = "Location is required";
    }
    setSelectedLocation(values.location);
  };

  const handleCategory = (event, value) => {
    values.category = parseInt(event.target.value);
    if (values.category > 0) {
      delete errors["category"];
    } else {
      errors.category = "Category is required";
    }
    setSelectedCategory(values.category);
  };

  const handleCountry = (event, value) => {
    values.country = parseInt(event.target.value);
    if (values.country < 0) {
      errors.country = "Country is required";
    } else {
      delete errors["country"];
    }
    setSelectedCountry(values.country);
    if (values.country === 235) {
      setSelectedState("--");
    } else {
      setSelectedState("");
    }
    handleState(undefined, undefined);
  };

  const handleState = (event, value) => {
    values.state = event ? event.target.value : undefined;
    console.log(values.state);
    if (!values.state || values.state === "") {
      errors.state = "State/Province is required";
    } else if (values.state === "--") {
      errors.state = "State is required";
    } else {
      delete errors["state"];
    }
    setSelectedState(values.state);
  };

  const handleChooseFile = (event) => {
    setSelectedFile(event.target.files[0]);
    values.imageFile = event.target.files[0];
  };

  function sleep(delay = 0) {
    return new Promise((resolve) => {
      setTimeout(resolve, delay);
    });
  }

  useEffect(() => {
    let active = true;

    (async () => {
      if (airportKeyword && airportKeyword.length > 1) {
        const response = await onGetAirports(airportKeyword);
        await sleep(1);
        const airports = response.data;
        if (active) {
          setAirportOptions(airports);
        }
      } else {
        setAirportOptions([]);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading, airportKeyword]);

  useEffect(() => {
    if (!open || airportKeyword < 2) {
      setAirportOptions([]);
    }
  }, [open]);

  return (
    <Box component="main" className={classes.boxWrapper}>
      <Paper elevation={3} className={classes.container}>
        <Form>
          <div className={classes.form}>
            <Autocomplete
              required
              fullWidth
              open={open}
              onOpen={() => {
                setOpen(true);
              }}
              onClose={() => {
                setOpen(false);
              }}
              loading={loading && airportOptions.length > 1}
              noOptionsText={"Start typing to search for airports"}
              options={airportOptions}
              getOptionLabel={(option) => option.airport}
              getOptionSelected={(option, value) =>
                option.airport === value.airport
              }
              onChange={(event, value) => {
                handleAirport(event, value);
              }}
              onInputChange={(event, value) => {
                setAirportKeyword(value);
              }}
              renderInput={(params) => (
                <CssTextField
                  required
                  fullWidth
                  id="airport"
                  name="airport"
                  label="Airport"
                  {...params}
                  variant="outlined"
                  error={touched.airport && errors.airport}
                  helperText={
                    touched.airport && errors.airport
                      ? errors.airport
                      : "Start typing to search for airport"
                  }
                  InputProps={{
                    ...params.InputProps,
                    type: "search",
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalAirportIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <React.Fragment>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </div>
          <div className={classes.form}>
            <Label for="contactInfo">CONTACT INFORMATION</Label>
          </div>
          <div className={classes.formItem}>
            <CssTextField
              fullWidth
              required
              id="firstName"
              name="firstName"
              label="First Name"
              value={values.firstName}
              onChange={handleChange}
              variant="outlined"
              inputProps={{ maxLength: 64 }}
              error={touched.firstName && errors.firstName}
              helperText={
                touched.firstName && errors.firstName ? errors.firstName : ""
              }
            />
            <CssTextField
              fullWidth
              required
              id="lastName"
              name="lastName"
              label="Last Name"
              value={values.lastName}
              onChange={handleChange}
              variant="outlined"
              inputProps={{ maxLength: 64 }}
              error={touched.lastName && errors.lastName}
              helperText={
                touched.lastName && errors.lastName ? errors.lastName : ""
              }
            />
          </div>
          <div className={classes.formItem}>
            <CssTextField
              fullWidth
              required
              id="email"
              name="email"
              label="Email"
              value={values.email}
              onChange={handleChange}
              variant="outlined"
              inputProps={{ maxLength: 128 }}
              error={touched.email && errors.email}
              helperText={touched.email && errors.email ? errors.email : ""}
            />

            <CssTextField
              fullWidth
              required
              id="emailConfirm"
              name="emailConfirm"
              label="Confirm Email"
              value={values.emailConfirm}
              onChange={handleChange}
              variant="outlined"
              inputProps={{ maxLength: 128 }}
              error={touched.emailConfirm && errors.emailConfirm}
              helperText={
                touched.emailConfirm && errors.emailConfirm
                  ? errors.emailConfirm
                  : ""
              }
            />
          </div>
          <div className={classes.form}>
            <CssTextField
              required
              fullWidth
              id="phone"
              name="phone"
              label="Phone Number"
              type="number"
              placeholder="6025551234"
              value={values.phone}
              onChange={handleChange}
              variant="outlined"
              inputProps={{ maxLength: 64 }}
              error={touched.phone && errors.phone}
              helperText={
                touched.phone && errors.phone
                  ? errors.phone
                  : "Numbers only, no special characters. (e.g. 6025551234)"
              }
            />
          </div>
          {/* <MuiPhoneNumber
              required
              id="phone"
              name="phone"
              label="Phone Number"
              autoFormat={false}
              // type="tel"
              defaultCountry={"us"}
              // disableCountryCode={true}
              value={phone}
              onChange={handlePhone}
              variant="outlined"
              error={touched.phone && errors.phone}
              helperText={touched.phone && errors.phone ? errors.phone : ""}
            /> */}
          <div className={classes.form}>
            <Label for="shippingAddress">SHIPPING ADDRESS</Label>
            <br />
            <Label>
              Please enter the address where you would like the item to be
              shipped
            </Label>
          </div>
          <div className={classes.form}>
            <CssTextField
              select
              fullWidth
              required
              id="country"
              name="country"
              label="Country"
              defaultValue={235}
              // value={values.country.name}
              value={selectedCountry}
              onChange={(event, value) => {
                handleCountry(event, value);
              }}
              variant="outlined"
              SelectProps={{
                native: true,
              }}
              error={touched.country && errors.country}
              helperText={
                touched.country && errors.country ? errors.country : ""
              }
            >
              {countries
                .sort((countryA, countryB) => {
                  return countryA.name.localeCompare(countryB.name);
                })
                .map((option) => (
                  <option key={option.country_id} value={option.country_id}>
                    {option.name}
                  </option>
                ))}
            </CssTextField>
          </div>
          <div className={classes.formItem}>
            <CssTextField
              fullWidth
              required
              id="streetAddress"
              name="streetAddress"
              label="Street Address"
              value={values.address}
              onChange={handleChange}
              variant="outlined"
              inputProps={{ maxLength: 128 }}
              error={touched.streetAddress && errors.streetAddress}
              helperText={
                touched.streetAddress && errors.streetAddress
                  ? errors.streetAddress
                  : ""
              }
            />
            <CssTextField
              fullWidth
              required
              id="city"
              name="city"
              label="City"
              value={values.city}
              onChange={handleChange}
              variant="outlined"
              inputProps={{ maxLength: 64 }}
              error={touched.city && errors.city}
              helperText={touched.city && errors.city ? errors.city : ""}
            />
          </div>
          <div className={classes.formItem}>
            {selectedCountry != 235 ? (
              <CssTextField
                fullWidth
                required
                id="state"
                name="state"
                label="State/Province"
                value={selectedState}
                onChange={handleState}
                variant="outlined"
                inputProps={{ maxLength: 32 }}
                error={touched.state && errors.state}
                helperText={touched.state && errors.state ? errors.state : ""}
              />
            ) : (
              <CssTextField
                select
                fullWidth
                required
                id="state"
                name="state"
                label="State"
                value={selectedState}
                onChange={(event, value) => {
                  handleState(event, value);
                }}
                variant="outlined"
                SelectProps={{
                  native: true,
                }}
                error={touched.state && errors.state}
                helperText={touched.state && errors.state ? errors.state : ""}
              >
                {usstates.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.name}
                  </option>
                ))}
              </CssTextField>
            )}
            <CssTextField
              fullWidth
              required
              id="zip"
              name="zip"
              label="Zip/Postal Code"
              value={values.zip}
              onChange={handleChange}
              variant="outlined"
              inputProps={{ maxLength: 16 }}
              error={touched.zip && errors.zip}
              helperText={touched.zip && errors.zip ? errors.zip : ""}
            />
          </div>
          <div className={classes.form}>
            <Label for="incidentInformation">INCIDENT INFORMATION</Label>
          </div>
          <div className={classes.form}>
            {/* <CssTextField
              select
              fullWidth
              required
              id="incidentType"
              name="incidentType"
              label="Incident Type"
              value={values.incidentType.label}
              onChange={(event, value) => {
                values.incidentType = parseInt(event.target.value);
              }}
              variant="outlined"
              SelectProps={{
                native: true,
              }}
              error={touched.incidentType && errors.incidentType}
              helperText={
                touched.incidentType && errors.incidentType
                  ? errors.incidentType
                  : ""
              }
            >
              {incidents.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </CssTextField> */}
            <CssTextField
              select
              fullWidth
              required
              id="category"
              name="category"
              label="Category"
              value={selectedCategory}
              onChange={(event, value) => {
                handleCategory(event, value);
              }}
              variant="outlined"
              SelectProps={{
                native: true,
              }}
              error={touched.category && errors.category}
              helperText={
                touched.category && errors.category ? errors.category : ""
              }
            >
              {categories.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </CssTextField>
          </div>
          <div className={classes.formItem}>
            <MuiThemeProvider theme={customTheme}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  disableToolbar
                  required
                  format="MM/dd/yyyy"
                  margin="normal"
                  id="date"
                  label="Date"
                  disableFuture={true}
                  // maxDate={new Date()}
                  value={selectedDate}
                  onChange={handleDate}
                  onClick={() => setOpenDate(true)}
                  onClose={() => setOpenDate(false)}
                  open={openDate}
                  autoComplete="off"
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                  inputVariant="outlined"
                  error={touched.date && errors.date}
                  helperText={touched.date && errors.date ? errors.date : ""}
                />
              </MuiPickersUtilsProvider>
            </MuiThemeProvider>
            <MuiThemeProvider theme={customTheme}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardTimePicker
                  required
                  fullWidth
                  margin="normal"
                  id="time"
                  allowKeyboardControl={false}
                  ampm={true}
                  label="Approximate time"
                  value={selectedTime}
                  onChange={handleTime}
                  onClick={() => setOpenTime(true)}
                  onClose={() => setOpenTime(false)}
                  open={openTime}
                  keyboardIcon={<Schedule />}
                  autoComplete="off"
                  KeyboardButtonProps={{
                    "aria-label": "change time",
                  }}
                  inputVariant="outlined"
                  error={touched.time && errors.time}
                  helperText={touched.time && errors.time ? errors.time : ""}
                />
              </MuiPickersUtilsProvider>
            </MuiThemeProvider>
          </div>
          <div className={classes.formItem}>
            <CssTextField
              select
              fullWidth
              required
              id="location"
              name="location"
              label="Location"
              value={selectedLocation}
              onChange={(event, value) => {
                handleLocation(event, value);
              }}
              variant="outlined"
              SelectProps={{
                native: true,
              }}
              error={touched.location && errors.location}
              helperText={
                touched.location && errors.location ? errors.location : ""
              }
            >
              {locations.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </CssTextField>
            <CssTextField
              fullWidth
              multiline
              required
              id="locationDescription"
              name="locationDescription"
              label="Location Description"
              value={values.description1}
              onChange={handleChange}
              variant="outlined"
              inputProps={{ maxLength: 512 }}
              error={touched.locationDescription && errors.locationDescription}
              helperText={
                touched.locationDescription && errors.locationDescription
                  ? errors.locationDescription
                  : "Terminal/Concourse is critical information, or any other location description"
              }
            />
          </div>
          <div className={classes.formItem}>
            <CssTextField
              select
              fullWidth
              id="airline"
              name="airline"
              label="Airline(Optional)"
              value={values.airline}
              onChange={(event, value) => {
                values.airline = parseInt(event.target.value);
              }}
              variant="outlined"
              SelectProps={{
                native: true,
              }}
              error={touched.airline && errors.airline}
              helperText={
                touched.airline && errors.airline ? errors.airline : ""
              }
            >
              {airlines.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </CssTextField>
            <CssTextField
              fullWidth
              id="flight"
              name="flight"
              label="Flight#(Optional)"
              value={values.flight}
              onChange={handleChange}
              variant="outlined"
              inputProps={{ maxLength: 16 }}
            />
          </div>
          <div className={classes.form}>
            <Label for="detailedDescription">
              DETAILED DESCRIPTION OF THE ITEM
            </Label>
            <Label>
              Thousands of items are lost daily, and many look alike. List
              details such as: color, brand and/or model, serial, user names,
              background images, any unique markings.
            </Label>
            <CssTextField
              fullWidth
              multiline
              required
              id="itemDescription"
              name="itemDescription"
              label="Item Description"
              value={values.description2}
              onChange={handleChange}
              variant="outlined"
              inputProps={{ maxLength: 1024 }}
              error={touched.itemDescription && errors.itemDescription}
              helperText={
                touched.itemDescription && errors.itemDescription
                  ? errors.itemDescription
                  : "Make, model, color, serial number is all critical information, or any other special markings or details"
              }
            />
          </div>
          <div className={classes.form}>
            <Label>Upload an image related to your item (Optional)</Label>
            <section>
              <Button
                variant="contained"
                component="label"
                style={{
                  backgroundColor: colors.primary,
                  color: "white",
                  outlineColor: colors.primary,
                  borderColor: colors.primary,
                  marginLeft: "auto",
                  marginRight: "15px",
                }}
              >
                Choose File
                <input
                  id="imageFile"
                  name="imageFile"
                  type="file"
                  accept="image/*"
                  onChange={handleChooseFile}
                  hidden
                />
              </Button>
              <Label style={{ color: "grey" }}>
                {selectedFile ? selectedFile.name : "No file selected"}
              </Label>
            </section>
          </div>
          <div className={classes.form}>
            <Label>
              By clicking submit you agree to our{" "}
              <a href={"https://traista.com/terms.html"} target="_blank">
                {" "}
                Terms & Conditions
              </a>{" "}
              and authorize Traista to act as your return shipping agent. Final
              verification and proof of ownership is required by the local Lost
              & Found agency where your items may be stored. After a successful
              match has been found, you will receive an email with a quote for
              return shipping. Please allow 3-5 business days for item matching.
              All property not claimed within 30 days is considered abandoned by
              law and may be recycled, auctioned, or otherwise discarded.
              <br /> Lost and Found{" "}
              <a href={"https://traista.com/claims-faq/"} target="_blank">
                Claims FAQ
              </a>
              {". "}
            </Label>
          </div>
          <div className={classes.form}>
            <Button
              variant="contained"
              style={{
                backgroundColor: colors.primary,
                color: "white",
                outlineColor: colors.primary,
                borderColor: colors.primary,
                marginLeft: "auto",
                marginRight: "15px",
              }}
              disabled={isSubmitting}
              onClick={handleSubmit}
            >
              Submit
            </Button>
            {isSubmitting ? (
              <Label style={{ color: "grey" }}>Processing claim...</Label>
            ) : (
              ""
              // <Label>{JSON.stringify(values)}</Label>
            )}
            {touched.itemDescription && Object.keys(errors).length > 0 ? (
              <Label style={{ color: "red" }}>
                Missing required information!
              </Label>
            ) : (
              ""
            )}
          </div>
          <p />
          <p />
        </Form>
      </Paper>
    </Box>
  );
};

const FormikApp = withFormik({
  mapPropsToValues({
    airport,
    firstName,
    lastName,
    email,
    emailConfirm,
    phone,
    streetAddress,
    city,
    zip,
    state,
    country,
    // incidentType,
    category,
    date,
    time,
    location,
    locationDescription,
    itemDescription,
    imageFile,
  }) {
    const selectedValues = {
      airport,
      firstName,
      lastName,
      email,
      emailConfirm,
      phone,
      streetAddress,
      city,
      zip,
      state,
      country,
      // incidentType,
      category,
      date,
      time,
      location,
      locationDescription,
      itemDescription,
      imageFile,
    };
    // console.log(JSON.stringify(selectedValues));
    return selectedValues;
  },
  validationSchema: Yup.object().shape({
    airport: Yup.number().positive("Airport is required"),
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    email: Yup.string().email().required("Email is required"),
    emailConfirm: Yup.string()
      .email()
      .test("email-match", "Emails must match", function (value) {
        return this.parent.email === value;
      }),
    phone: Yup.string().required("Phone Number is required"),
    streetAddress: Yup.string().required("Street Address is required"),
    city: Yup.string().required("City is required"),
    zip: Yup.string()
      .max(16, "Maximum 16 characters allowed")
      .required("Zip/Postal Code is required"),
    country: Yup.number().positive().required("Country is required"),
    state: Yup.string().notOneOf(["--"]).required("State is required"),
    // incidentType: Yup.number().positive().required("Incident Type is required"),
    category: Yup.number().positive("Category is required"),
    date: Yup.string().required("Date is required"),
    time: Yup.string().required("Time is required"),
    location: Yup.number().positive("Location is required"),
    locationDescription: Yup.string()
      .min(10, "Location description should be minimum 10 characters long")
      .required("Location Description is required"),
    itemDescription: Yup.string()
      .min(
        32,
        "Description is too short. There could be many other similar lost items and more information will better help in matching yours."
      )
      .required("Item Description is required"),
  }),
  async handleSubmit(values, { props, resetForm, setErrors, setSubmitting }) {
    setSubmitting(true);
    await props.onSubmit(values);
    // resetForm();
    setSubmitting(false);
  },
  displayName: "TSAForm{HOC}",
})(TSAForm);

export default FormikApp;



// WEBPACK FOOTER //
// ./src/components/TSAForm.js